<!-- SETUP — the machine's own details, and the switches that make it usable.
     The accessibility switches are not buried under an "Accessibility" heading
     nobody opens: they sit with the rest of the machine's settings, because
     that is where someone looks when the app is not working for them. -->
<template>
  <IonPage>
    <AppHeader title="Setup" back="/tabs/machine" />
    <IonContent :fullscreen="true">

      <IonList inset class="list-inset">
        <IonListHeader>
          <IonLabel>
            <h3>This machine</h3>
            <p>Printed at the top of every fax it sends.</p>
          </IonLabel>
        </IonListHeader>
        <IonItem>
          <IonInput
            label="Machine name" label-placement="stacked" :value="m.tsi"
            placeholder="MUM'S GOOD MACHINE" autocapitalize="characters" maxlength="20"
            @ionInput="set('tsi', $event.target.value.toUpperCase())"
          />
        </IonItem>
        <IonItem>
          <IonInput
            label="Fax number" label-placement="stacked" :value="m.number"
            type="tel" inputmode="tel" maxlength="20"
            helper-text="Printed on the header line. Nothing ever dials it."
            @ionInput="set('number', $event.target.value)"
          />
        </IonItem>
        <IonItem lines="none">
          <IonSelect label="Date format" :interface-options="{ header: 'Date format' }" :value="m.dateFormat" @ionChange="set('dateFormat', $event.detail.value)">
            <IonSelectOption value="dmy">31/12</IonSelectOption>
            <IonSelectOption value="mdy">12/31</IonSelectOption>
          </IonSelect>
        </IonItem>
      </IonList>

      <!-- Where the worry actually occurs, rather than in a policy nobody opens. -->
      <p class="silkscreen says pad">
        {{ say(
          'NOTHING LEAVES THIS MACHINE.',
          'Nothing you type here leaves this device.',
        ) }}
      </p>

      <IonList inset class="list-inset">
        <IonListHeader><IonLabel><h3>Servicing</h3></IonLabel></IonListHeader>
        <!-- SHAKE CARTRIDGE IS THE ONE CONTROL IN THE APP THAT NAMES A
             PHYSICAL ACTION, so it is the one that has to answer with one. The
             number going up is the fact; the row jolting sideways under the
             thumb is the acknowledgement that a hand did it — and at a full
             cartridge, where the number CANNOT go up, the row says so instead
             of silently doing nothing, which is the "at the maximum" edge case
             that breaks a control in front of a customer. -->
        <IonItem :class="{ shaking }">
          <!-- The button sits UNDER the words, not beside them: side by side,
               large text squeezed the toner line into a five-line sliver. -->
          <IonLabel class="ion-text-wrap">
            <h3>Toner</h3>
            <p>{{ tonerLine }}</p>
            <IonButton class="toner-btn" fill="outline" size="small" @click="shake">
              {{ say('SHAKE CARTRIDGE', 'Shake the cartridge') }}
            </IonButton>
          </IonLabel>
        </IonItem>
        <IonItem v-if="m.jammed">
          <IonLabel><h3>Paper jam</h3><p>Nothing will print until it is cleared.</p></IonLabel>
          <IonButton slot="end" color="danger" @click="unjam">
            {{ say('CLEAR JAM', 'Clear the jam') }}
          </IonButton>
        </IonItem>
        <IonItem lines="none">
          <IonLabel><h3>Pages printed</h3><p>{{ m.pageCount || 0 }}</p></IonLabel>
        </IonItem>
      </IonList>

      <!-- EACH SWITCH SAYS WHAT IT DOES, BESIDE ITSELF.
           The three used to be bare words with one silkscreened paragraph
           underneath the whole list, describing two of them and not the third —
           so the person reaching for LARGE CONTROLS because the app is not
           working for them had to read a block of uppercase to find out which
           switch was theirs. A settings row with no second line is a row you
           have to try in order to understand. -->
      <IonList inset class="list-inset">
        <IonListHeader>
          <IonLabel>
            <h3>How it behaves</h3>
            <p>Three switches that change the machine itself.</p>
          </IonLabel>
        </IonListHeader>
        <IonItem>
          <IonToggle :checked="m.speaker" @ionChange="set('speaker', $event.detail.checked)">
            Speaker
            <div class="setting-note">The modem screech while a page goes through.</div>
          </IonToggle>
        </IonItem>
        <IonItem>
          <IonToggle :checked="m.largeControls" @ionChange="set('largeControls', $event.detail.checked)">
            Large controls
            <div class="setting-note">
              Replaces the machine drawing on the home screen with a plain,
              high-contrast list of the same three destinations.
            </div>
          </IonToggle>
        </IonItem>
        <IonItem lines="none">
          <IonToggle :checked="m.plainEnglish" @ionChange="set('plainEnglish', $event.detail.checked)">
            Plain English
            <div class="setting-note">
              Stops the machine shouting. Ordinary sentences, sentence case, and
              nothing said in fax jargon.
            </div>
          </IonToggle>
        </IonItem>
      </IonList>

      <IonList inset class="list-inset">
        <IonItem button :detail="true" href="/privacy"><IonLabel>Privacy</IonLabel></IonItem>
        <IonItem button :detail="true" href="/support"><IonLabel>Support</IonLabel></IonItem>
        <IonItem lines="none"><IonLabel>Model JTF-1</IonLabel></IonItem>
      </IonList>

      <div class="tail" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import {
  IonPage, IonContent, IonList, IonListHeader, IonItem, IonLabel, IonInput,
  IonToggle, IonButton, IonSelect, IonSelectOption,
} from '@ionic/vue';
import { AppHeader } from '../core/ui';
import { announce } from '../core/a11y.js';
import { haptic } from '../core/util.js';
import { storeReady } from '../store.js';
import { machine, ensureMachine, updateMachine, shakeCartridge, clearJam } from '../fax/machine.js';
import { say } from '../fax/voice.js';

const m = machine;
const shaking = ref(false);
const full = computed(() => Math.round(m.value.toner ?? 0) >= 100);

const tonerLine = computed(() => (full.value
  ? '100% — full. Nothing to shake loose.'
  : `${Math.round(m.value.toner)}% — pages come out paler as it empties.`));

// A toggle flipping IS its own feedback, so there is no toast here — only the
// tick that makes a switch on a screen feel like a switch on a case.
const set = (k, v) => { haptic('tick'); updateMachine({ [k]: v }); };

let shakeTimer = 0;
function shake() {
  // At the maximum, say so. The old version fired, changed nothing visible,
  // and left the person tapping a control that had quietly stopped working.
  if (full.value) {
    haptic('warning');
    announce('The cartridge is already full.');
    return;
  }
  haptic('heavy');
  shaking.value = false;
  // One frame at rest, or re-tapping mid-animation restarts nothing and the
  // second shake is invisible.
  requestAnimationFrame(() => { shaking.value = true; });
  clearTimeout(shakeTimer);
  shakeTimer = setTimeout(() => { shaking.value = false; }, 360);
  const next = shakeCartridge();
  announce(`Cartridge shaken. Toner ${Math.round(next?.toner ?? 0)} per cent.`);
}

function unjam() {
  // The row vanishing is what a sighted person sees; `pop` is what it feels
  // like, and the announcement is the only version of it a screen reader gets.
  haptic('pop');
  clearJam();
  announce('Jam cleared. The machine is ready.');
}

onMounted(async () => { await storeReady; await ensureMachine(); });
</script>

<style scoped>
.toner-btn { margin: var(--sp-2) 0 var(--sp-1); }
</style>
